/* ═══════════════════════════════════════════════════════════════════════════
   La politique d'allocation des voix — PURE, donc testable sans navigateur.

   C'est ici que se joue la différence entre un jeu qui sonne bien sous charge
   et un jeu qui crache. Quatre règles, chacune contre une panne précise
   (PLAN.md §5.1) :

   1. PLAFOND GLOBAL — 32 voix sur desktop, 16 sur mobile. Un plafond unique
      dimensionné pour desktop fait craquer le CPU audio d'un téléphone.

   2. PLAFONDS PAR CATÉGORIE — 4 tirs joueur, 6 explosions, 2 interface. Sans
      eux, une pluie d'explosions vole la voix du son de dégât joueur, qui est
      l'information de gameplay la plus critique du jeu. Une catégorie ne peut
      jamais affamer les autres.

   3. FENÊTRE ANTI-DOUBLON de 40 ms — la mesure la plus payante et la plus
      oubliée. Si douze ennemis tirent sur la même frame, jouer douze fois le
      même échantillon produit un renforcement cohérent de +21 dB et un
      flanging désagréable : ça sonne comme un bug, pas comme une vague. On
      refuse l'instance et on monte légèrement le gain de celle en cours.

   4. VOL PAR PRIORITÉ — quand tout est plein, on coupe la voix la moins
      prioritaire et la plus ancienne, jamais celle qui vient de démarrer.

   Ce module ne connaît ni Web Audio, ni le DOM, ni le temps réel : il reçoit un
   horodatage et rend des décisions. C'est ce qui permet de vérifier qu'un
   rideau de balles ne rend pas le jeu muet, sans ouvrir un navigateur.
   ═══════════════════════════════════════════════════════════════════════════ */

export const Category = {
  PlayerShot: 0,
  EnemyShot: 1,
  Explosion: 2,
  PlayerDamage: 3,
  Ui: 4,
  Pickup: 5,
  /** Arme SECONDAIRE — un budget de voix À PART du tir primaire. Sinon une salve
   *  (jusqu'à 12 volées) se ferait voler ses voix par le tir auto (10/s), et on
   *  n'entendrait qu'un hachis. */
  PlayerSecondary: 6,
} as const;

export type CategoryId = (typeof Category)[keyof typeof Category];

/** Plafonds par catégorie. `PlayerDamage` n'est volontairement PAS plafonné
 *  bas : c'est le seul son que le joueur doit entendre quoi qu'il arrive. */
export const CATEGORY_CAPS: Readonly<Record<number, number>> = {
  [Category.PlayerShot]: 4,
  [Category.EnemyShot]: 6,
  [Category.Explosion]: 6,
  [Category.PlayerDamage]: 3,
  [Category.Ui]: 2,
  [Category.Pickup]: 4,
  [Category.PlayerSecondary]: 5,
};

/** Priorités. Plus haut = plus difficile à voler. */
export const CATEGORY_PRIORITY: Readonly<Record<number, number>> = {
  [Category.PlayerDamage]: 100,
  [Category.Ui]: 80,
  [Category.Explosion]: 50,
  [Category.Pickup]: 40,
  [Category.EnemyShot]: 30,
  // La secondaire passe DEVANT le tir primaire : un déclenchement délibéré vaut
  // plus qu'un tir automatique.
  [Category.PlayerSecondary]: 25,
  [Category.PlayerShot]: 20,
};

/** Fenêtre de refus des doublons, en millisecondes. */
export const DEDUPE_MS = 40;

export interface VoiceSlot {
  active: boolean;
  /** Identifiant du son joué. */
  soundId: string;
  category: number;
  priority: number;
  /** Horodatage de déclenchement, en millisecondes. */
  startedAt: number;
  /** Durée prévue, en millisecondes. */
  durationMs: number;
  /** Compteur d'instances refusées et repliées sur celle-ci. */
  reinforced: number;
}

export interface PlayDecision {
  /** `-1` si le son est refusé. */
  readonly slot: number;
  /** Slot dont la voix a été volée, `-1` sinon. Il doit être coupé avec une
   *  RAMPE de gain, jamais un arrêt sec — un `stop()` brut produit un clic. */
  readonly stolen: number;
  /** Le son a été refusé parce qu'un identique vient de démarrer. */
  readonly deduped: boolean;
  /** Gain à appliquer, déjà pondéré par le renforcement. */
  readonly gain: number;
}

const REFUSED: PlayDecision = { slot: -1, stolen: -1, deduped: false, gain: 0 };

export class VoicePool {
  readonly slots: VoiceSlot[] = [];
  private readonly capacity: number;

  constructor(capacity = 32) {
    this.capacity = capacity;
    for (let i = 0; i < capacity; i++) {
      this.slots.push({
        active: false,
        soundId: '',
        category: -1,
        priority: 0,
        startedAt: 0,
        durationMs: 0,
        reinforced: 0,
      });
    }
  }

  /** Libère les voix dont la durée est écoulée. À appeler avant toute décision. */
  expire(now: number): void {
    for (const s of this.slots) {
      if (s.active && now - s.startedAt >= s.durationMs) s.active = false;
    }
  }

  activeCount(category?: number): number {
    let n = 0;
    for (const s of this.slots) {
      if (!s.active) continue;
      if (category === undefined || s.category === category) n++;
    }
    return n;
  }

  /**
   * Décide si un son peut être joué, et où.
   *
   * `baseGain` est le gain nominal ; la valeur rendue peut être légèrement
   * supérieure quand des doublons ont été repliés sur la voix en cours.
   */
  request(
    soundId: string,
    category: number,
    now: number,
    durationMs: number,
    baseGain = 1,
  ): PlayDecision {
    this.expire(now);

    // 1. Anti-doublon. On cherche une instance IDENTIQUE démarrée dans la
    //    fenêtre : la superposer produirait un renforcement cohérent, pas une
    //    impression de nombre.
    for (const s of this.slots) {
      if (!s.active || s.soundId !== soundId) continue;
      if (now - s.startedAt < DEDUPE_MS) {
        s.reinforced++;
        return { slot: -1, stolen: -1, deduped: true, gain: 0 };
      }
    }

    const priority = CATEGORY_PRIORITY[category] ?? 10;
    const cap = CATEGORY_CAPS[category] ?? this.capacity;

    // 2. Plafond de catégorie. Au-delà, on ne vole QUE dans sa propre
    //    catégorie : une avalanche d'explosions ne doit jamais pouvoir museler
    //    le son de dégât joueur.
    if (this.activeCount(category) >= cap) {
      const victim = this.oldestIn(category);
      if (victim < 0) return REFUSED;
      return this.assign(victim, soundId, category, priority, now, durationMs, baseGain, victim);
    }

    // 3. Slot libre ?
    const free = this.slots.findIndex((s) => !s.active);
    if (free >= 0) {
      return this.assign(free, soundId, category, priority, now, durationMs, baseGain, -1);
    }

    // 4. Pool saturé : on vole la voix la moins prioritaire, et à priorité
    //    égale la plus ancienne. Un son strictement plus prioritaire que le
    //    demandeur n'est jamais volé — c'est ce qui protège l'information
    //    critique.
    let victim = -1;
    let worst = priority;
    let oldest = Infinity;
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i]!;
      if (s.priority > worst) continue;
      if (s.priority < worst || s.startedAt < oldest) {
        worst = s.priority;
        oldest = s.startedAt;
        victim = i;
      }
    }
    if (victim < 0) return REFUSED;
    return this.assign(victim, soundId, category, priority, now, durationMs, baseGain, victim);
  }

  private oldestIn(category: number): number {
    let idx = -1;
    let oldest = Infinity;
    for (let i = 0; i < this.slots.length; i++) {
      const s = this.slots[i]!;
      if (!s.active || s.category !== category) continue;
      if (s.startedAt < oldest) {
        oldest = s.startedAt;
        idx = i;
      }
    }
    return idx;
  }

  private assign(
    i: number,
    soundId: string,
    category: number,
    priority: number,
    now: number,
    durationMs: number,
    baseGain: number,
    stolen: number,
  ): PlayDecision {
    const s = this.slots[i]!;
    s.active = true;
    s.soundId = soundId;
    s.category = category;
    s.priority = priority;
    s.startedAt = now;
    s.durationMs = durationMs;
    s.reinforced = 0;
    return { slot: i, stolen, deduped: false, gain: baseGain };
  }

  /** Gain de renforcement d'une voix ayant absorbé des doublons.
   *  Croissance logarithmique et PLAFONNÉE : douze ennemis qui tirent doivent
   *  sonner « plus fort », jamais douze fois plus fort. */
  static reinforcementGain(count: number): number {
    if (count <= 0) return 1;
    const g = 1 + Math.log10(1 + count) * 0.45;
    return g > 1.6 ? 1.6 : g;
  }

  releaseAll(): void {
    for (const s of this.slots) s.active = false;
  }

  get size(): number {
    return this.capacity;
  }
}
